// ── BCHNostr — Podcasts ───────────────────────────────────────────
//
// BCHNostr is a DISCOVERY, DISCUSSION, PROMOTION and MONETIZATION layer
// for podcasts. It is deliberately NOT a podcast host:
//
//   IDENTITY       Nostr (the creator's existing key — no second account)
//   AUDIO HOSTING  the creator's existing host, via their RSS feed
//   DISCOVERY      BCHNostr
//   DISCUSSION     Nostr (NIP-22 comments on a NIP-73 podcast:item:guid)
//   PAYMENTS       Bitcoin Cash (the existing tip / boost / membership rails)
//   PROMOTION      the existing BCHNostr boost auction
//
// A podcast is a STANDALONE feature. The show's metadata lives in its own
// addressable event (kind 30078, d=bchnostr/podcast) — it is NOT tied to
// the Professional Profile (kind 30499) in any way. Discovery queries the
// show events directly.
//
// Episodes are NEVER published to Nostr and audio is NEVER copied or
// proxied. The RSS feed is the single source of truth; we cache a small
// amount of parsed metadata in localStorage so pages render instantly and
// we don't re-fetch the feed on every navigation.

import type { Event } from 'nostr-tools';
import { PLATFORM_FEE_ADDRESS } from './platform';

// ── Kinds & tags ──────────────────────────────────────────────────

/** Podcast show metadata. Addressable, authored by the creator. */
export const PODCAST_KIND = 30078;
/** Fixed d-tag — one show per pubkey. */
export const PODCAST_D_TAG = 'bchnostr/podcast';
/** Relay-indexable discovery tag every show carries. */
export const PODCAST_TAG = 'bch-podcast';
/** Prefix for the indexable category tag. */
export const PODCAST_CAT_PREFIX = 'bch-pod-cat-';

/** Podcast follow (a "subscribe" that is not a Nostr contact-list follow). */
export const PODCAST_FOLLOW_KIND = 30078;
export const PODCAST_FOLLOW_D_TAG = 'bchnostr/podcast-follows';
export const PODCAST_FOLLOW_TAG = 'bch-podcast-follow';

/** Episode discussion — NIP-22 comment rooted on a NIP-73 external id. */
export const PODCAST_COMMENT_KIND = 1111;

/** Podcast / episode promotion, riding the existing boost auction. */
export const PODCAST_BOOST_KIND = 30078;
export const PODCAST_BOOST_TAG = 'bch-podcast-boost';
export const PODCAST_BOOST_D_TAG_PREFIX = 'bchnostr/podcast-boost/';
export const PODCAST_BOOST_WALLET = PLATFORM_FEE_ADDRESS;
/** Same 6h window the rest of the app's boosts use. */
export const PODCAST_BOOST_DURATION = 6 * 3600;
export const PODCAST_BOOST_MIN_BID_USD = 0.25;
export const PODCAST_BOOST_BID_PRESETS = [
  { usd: 0.25, label: '$0.25', emoji: '\u26A1' },
  { usd: 1,    label: '$1',    emoji: '\u{1F525}' },
  { usd: 3,    label: '$3',    emoji: '\u{1F680}' },
  { usd: 5,    label: '$5',    emoji: '\u{1F451}' },
];
/** How many promoted shows/episodes are surfaced at once. */
export const PODCAST_BOOST_MAX_VISIBLE = 3;

// ── Categories ────────────────────────────────────────────────────

export const PODCAST_CATEGORIES: Array<{ id: string; label: string }> = [
  { id: 'bitcoin',    label: 'Bitcoin & Crypto' },
  { id: 'technology', label: 'Technology' },
  { id: 'business',   label: 'Business' },
  { id: 'news',       label: 'News & Politics' },
  { id: 'education',  label: 'Education' },
  { id: 'culture',    label: 'Society & Culture' },
  { id: 'comedy',     label: 'Comedy' },
  { id: 'health',     label: 'Health & Fitness' },
  { id: 'music',      label: 'Music' },
  { id: 'sports',     label: 'Sports' },
  { id: 'other',      label: 'Other' },
];

export function podcastCategoryLabel(id: string | undefined): string {
  return PODCAST_CATEGORIES.find(c => c.id === id)?.label || 'Other';
}

// ── Shape ─────────────────────────────────────────────────────────

/** A podcast show as published by its creator. */
export interface PodcastShow {
  /** Show title. */
  name: string;
  /** Plain-text description. */
  description: string;
  /** Cover art URL (https only). */
  image: string;
  /** RSS feed URL — the source of truth for episodes (https only). */
  feedUrl: string;
  /** Show website (https only). */
  website: string;
  /** Host names. */
  hosts: string[];
  /** Category id from PODCAST_CATEGORIES. */
  category: string;
  /** Optional dedicated BCH donation address for the show. */
  bch: string;
  /** Podcast GUID from the feed's <podcast:guid>, when present. */
  guid: string;
  /** Creator's pubkey — the show's Nostr identity. */
  pubkey: string;
  created_at: number;
  event: Event;
}

/** A single episode, parsed from the RSS feed. Never published to Nostr. */
export interface PodcastEpisode {
  /** Stable identifier — the RSS <guid>, or a derived fallback. */
  guid: string;
  title: string;
  /** Plain text (HTML stripped). */
  description: string;
  /** Unix seconds. 0 when the feed omits/mangles pubDate. */
  publishedAt: number;
  /** Direct audio URL from the creator's host. Playback goes here. */
  audioUrl: string;
  /** Seconds. 0 when unknown. */
  duration: number;
  /** Episode artwork, falling back to the show cover at render time. */
  image: string;
  /** Canonical episode page on the creator's site. */
  link: string;
  /** Episode number from <itunes:episode>, when present. */
  episodeNumber: number | null;
  /** Season number from <itunes:season>, when present. */
  seasonNumber: number | null;
}

/** Everything a feed fetch yields. */
export interface PodcastFeed {
  title: string;
  description: string;
  image: string;
  link: string;
  author: string;
  guid: string;
  episodes: PodcastEpisode[];
}

// ── URL sanitization ──────────────────────────────────────────────
//
// Everything below the RSS boundary is UNTRUSTED. A feed can put
// `javascript:`, `data:`, or a credential-stuffed URL in any field, and
// those values end up in href / src attributes. Every URL is funnelled
// through one of these before it reaches the DOM.

/** Allow only absolute http(s) URLs. Returns '' for anything else. */
export function safeUrl(raw: string | undefined | null): string {
  if (!raw || typeof raw !== 'string') return '';
  const value = raw.trim();
  if (!value) return '';
  try {
    const url = new URL(value);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return '';
    // Credentials in a URL are a phishing vector — never render one.
    if (url.username || url.password) return '';
    return url.toString();
  } catch {
    return '';
  }
}

/**
 * Like safeUrl, but tolerates a bare hostname ("example.com") the way the
 * business profile's website field does. Used for user-typed input only.
 */
export function safeUrlLoose(raw: string | undefined | null): string {
  if (!raw || typeof raw !== 'string') return '';
  const value = raw.trim();
  if (!value) return '';
  if (/^[a-z][a-z0-9+.-]*:/i.test(value)) return safeUrl(value);
  return safeUrl(`https://${value}`);
}

/** Only https feeds — an http feed is a mixed-content failure anyway. */
export function safeFeedUrl(raw: string | undefined | null): string {
  const url = safeUrlLoose(raw);
  if (!url) return '';
  return url.startsWith('https://') ? url : '';
}

// ── Text sanitization ─────────────────────────────────────────────

/**
 * Turn untrusted feed markup into plain text.
 *
 * Feed descriptions are routinely full of HTML. We never render it: tags
 * are stripped, entities decoded manually (NOT via innerHTML, which would
 * execute embedded markup), and the result is length-capped so one
 * pathological feed can't blow up a render or localStorage.
 */
export function stripHtml(raw: string | undefined | null, maxLength = 4000): string {
  if (!raw || typeof raw !== 'string') return '';
  let text = raw;
  // Drop entire script/style blocks including their contents.
  text = text.replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, ' ');
  // Turn block-level boundaries into newlines so paragraphs survive.
  text = text.replace(/<\/(p|div|li|h[1-6]|blockquote)\s*>/gi, '\n');
  text = text.replace(/<br\s*\/?>/gi, '\n');
  // Remove every remaining tag.
  text = text.replace(/<[^>]*>/g, '');
  text = decodeEntities(text);
  // Collapse runaway whitespace.
  text = text.replace(/[ \t\u00a0]+/g, ' ').replace(/\n{3,}/g, '\n\n').trim();
  return text.length > maxLength ? text.slice(0, maxLength).trimEnd() + '…' : text;
}

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
  mdash: '—', ndash: '–', hellip: '…', rsquo: '\u2019', lsquo: '\u2018',
  ldquo: '\u201c', rdquo: '\u201d', trade: '™', copy: '©', reg: '®',
};

/**
 * Decode HTML entities WITHOUT touching innerHTML.
 *
 * The usual `el.innerHTML = s; return el.textContent` trick would execute
 * markup inside the string — exactly the payload path we're defending
 * against. Numeric escapes are clamped to valid code points.
 */
function decodeEntities(text: string): string {
  return text.replace(/&(#x?[0-9a-f]+|[a-z][a-z0-9]*);/gi, (match, entity: string) => {
    if (entity[0] === '#') {
      const isHex = entity[1] === 'x' || entity[1] === 'X';
      const code = parseInt(isHex ? entity.slice(2) : entity.slice(1), isHex ? 16 : 10);
      if (!Number.isFinite(code) || code <= 0 || code > 0x10ffff) return '';
      try { return String.fromCodePoint(code); } catch { return ''; }
    }
    const named = NAMED_ENTITIES[entity.toLowerCase()];
    return named !== undefined ? named : match;
  });
}

/** Trim a string and hard-cap its length. */
function clip(raw: string | undefined | null, max: number): string {
  if (!raw || typeof raw !== 'string') return '';
  const value = raw.trim();
  return value.length > max ? value.slice(0, max).trimEnd() : value;
}

// ── RSS parsing ───────────────────────────────────────────────────

/** Limits that keep a hostile or merely enormous feed harmless. */
export const MAX_EPISODES = 300;
const MAX_FEED_BYTES = 8 * 1024 * 1024; // 8 MB
const FEED_TIMEOUT_MS = 20000;

function firstText(parent: Element, ...selectors: string[]): string {
  for (const sel of selectors) {
    // getElementsByTagName is namespace-agnostic, which matters because
    // feeds are wildly inconsistent about itunes:/podcast: prefixes.
    const nodes = parent.getElementsByTagName(sel);
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      // Only direct-ish children — never reach into a nested <item>.
      if (node.closest('item') !== parent.closest('item')) continue;
      const value = node.textContent?.trim();
      if (value) return value;
    }
  }
  return '';
}

function attrOf(parent: Element, tagName: string, attr: string): string {
  const nodes = parent.getElementsByTagName(tagName);
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if (node.closest('item') !== parent.closest('item')) continue;
    const value = node.getAttribute(attr);
    if (value) return value.trim();
  }
  return '';
}

/**
 * Parse an iTunes duration, which may be "3600", "60:00", or "1:00:00".
 * Returns seconds, or 0 when unparseable.
 */
export function parseDuration(raw: string): number {
  const value = (raw || '').trim();
  if (!value) return 0;
  if (/^\d+(\.\d+)?$/.test(value)) {
    const secs = Math.floor(parseFloat(value));
    return secs > 0 && secs < 86400 * 7 ? secs : 0;
  }
  const parts = value.split(':').map(p => parseInt(p, 10));
  if (parts.some(p => !Number.isFinite(p) || p < 0)) return 0;
  let secs = 0;
  for (const part of parts) secs = secs * 60 + part;
  return secs > 0 && secs < 86400 * 7 ? secs : 0;
}

/** Parse an RSS pubDate into unix seconds. 0 when unparseable. */
function parseDate(raw: string): number {
  if (!raw) return 0;
  const ms = Date.parse(raw.trim());
  if (!Number.isFinite(ms)) return 0;
  const secs = Math.floor(ms / 1000);
  // Reject absurd dates (pre-1990 / far future) — usually a mangled feed.
  const now = Math.floor(Date.now() / 1000);
  if (secs < 631152000 || secs > now + 86400 * 365) return 0;
  return secs;
}

/**
 * Parse an RSS/Atom podcast feed document into a PodcastFeed.
 *
 * Every field is sanitized here so that callers — and anything that later
 * reads the cache — can treat a PodcastEpisode as safe to render.
 */
export function parseFeedXml(xml: string): PodcastFeed | null {
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(xml, 'application/xml');
  } catch {
    return null;
  }
  if (!doc || doc.getElementsByTagName('parsererror').length > 0) return null;

  const channel = doc.querySelector('channel') || doc.querySelector('feed');
  if (!channel) return null;

  const showImage =
    safeUrl(attrOf(channel, 'image', 'href')) ||
    safeUrl(firstText(channel, 'url')) ||
    safeUrl(attrOf(channel, 'itunes:image', 'href'));

  const feed: PodcastFeed = {
    title: clip(stripHtml(firstText(channel, 'title'), 200), 200),
    description: stripHtml(
      firstText(channel, 'description', 'itunes:summary', 'subtitle'), 3000,
    ),
    image: showImage,
    link: safeUrl(firstText(channel, 'link')) || safeUrl(attrOf(channel, 'link', 'href')),
    author: clip(stripHtml(firstText(channel, 'itunes:author', 'managingEditor'), 200), 200),
    guid: clip(firstText(channel, 'podcast:guid'), 100),
    episodes: [],
  };

  const items = doc.getElementsByTagName('item').length > 0
    ? doc.getElementsByTagName('item')
    : doc.getElementsByTagName('entry');

  const seen = new Set<string>();
  for (let i = 0; i < items.length && feed.episodes.length < MAX_EPISODES; i++) {
    const item = items[i];
    const episode = parseItem(item);
    if (!episode) continue;
    // De-duplicate by guid — feeds do repeat items, and re-publishing an
    // episode under the same guid must not create a second entry.
    if (seen.has(episode.guid)) continue;
    seen.add(episode.guid);
    feed.episodes.push(episode);
  }

  // Newest first. Episodes without a usable date sink to the bottom rather
  // than jumping to the top as if they were published at the epoch.
  feed.episodes.sort((a, b) => b.publishedAt - a.publishedAt);

  return feed;
}

function parseItem(item: Element): PodcastEpisode | null {
  // Audio URL: <enclosure url> is canonical; some feeds only have media:content.
  const audioUrl =
    safeUrl(attrOf(item, 'enclosure', 'url')) ||
    safeUrl(attrOf(item, 'media:content', 'url'));
  // No audio means it isn't a playable episode — skip it entirely.
  if (!audioUrl) return null;

  const title = clip(stripHtml(firstText(item, 'title'), 300), 300);
  const link = safeUrl(firstText(item, 'link')) || safeUrl(attrOf(item, 'link', 'href'));
  const rawGuid = clip(firstText(item, 'guid', 'id'), 200);
  // The guid is the episode's identity: it keys the cache, the discussion
  // thread, boosts, and analytics. Fall back to the audio URL so an
  // episode without a guid is still stable across refreshes.
  const guid = rawGuid || audioUrl;

  const episodeNumber = parseInt(firstText(item, 'itunes:episode', 'episode'), 10);
  const seasonNumber = parseInt(firstText(item, 'itunes:season', 'season'), 10);

  return {
    guid,
    title: title || 'Untitled episode',
    description: stripHtml(
      firstText(item, 'itunes:summary', 'description', 'content:encoded', 'summary'), 4000,
    ),
    publishedAt: parseDate(firstText(item, 'pubDate', 'published', 'updated')),
    audioUrl,
    duration: parseDuration(firstText(item, 'itunes:duration', 'duration')),
    image: safeUrl(attrOf(item, 'itunes:image', 'href')) || safeUrl(attrOf(item, 'image', 'href')),
    link,
    episodeNumber: Number.isFinite(episodeNumber) && episodeNumber > 0 ? episodeNumber : null,
    seasonNumber: Number.isFinite(seasonNumber) && seasonNumber > 0 ? seasonNumber : null,
  };
}

// ── Feed fetching ─────────────────────────────────────────────────

export type FeedErrorKind = 'invalid-url' | 'unreachable' | 'timeout' | 'not-a-feed' | 'too-large';

export interface FeedFetchResult {
  feed: PodcastFeed | null;
  error: FeedErrorKind | null;
  /** Human-readable message suitable for display. */
  message: string;
}

const FEED_ERROR_MESSAGES: Record<FeedErrorKind, string> = {
  'invalid-url': 'That doesn’t look like a valid https feed URL.',
  'unreachable': 'Could not reach that feed. Check the URL and try again.',
  'timeout': 'The feed took too long to respond.',
  'not-a-feed': 'That URL responded, but it isn’t a podcast RSS feed.',
  'too-large': 'That feed is too large to import.',
};

/**
 * Podcast hosts almost never send CORS headers, so a browser cannot read
 * the feed directly. We go through the shared read-only proxy. It fetches
 * text on our behalf and adds nothing to the request — no credentials, no
 * user data. Audio is NEVER proxied; only the small XML document is.
 */
function proxied(url: string): string {
  return `https://proxy.shakespeare.diy/?url=${encodeURIComponent(url)}`;
}

/**
 * Fetch and parse a podcast feed.
 *
 * Never throws: every failure mode a third-party feed can present
 * (offline, timeout, HTML error page, 40 MB of XML) comes back as a typed
 * error so callers can degrade gracefully instead of taking down the page.
 */
export async function fetchFeed(rawUrl: string): Promise<FeedFetchResult> {
  const url = safeFeedUrl(rawUrl);
  if (!url) return { feed: null, error: 'invalid-url', message: FEED_ERROR_MESSAGES['invalid-url'] };

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FEED_TIMEOUT_MS);

  try {
    const res = await fetch(proxied(url), { signal: controller.signal });
    if (!res.ok) {
      return { feed: null, error: 'unreachable', message: FEED_ERROR_MESSAGES.unreachable };
    }

    const length = parseInt(res.headers.get('content-length') || '', 10);
    if (Number.isFinite(length) && length > MAX_FEED_BYTES) {
      return { feed: null, error: 'too-large', message: FEED_ERROR_MESSAGES['too-large'] };
    }

    const text = await res.text();
    if (text.length > MAX_FEED_BYTES) {
      return { feed: null, error: 'too-large', message: FEED_ERROR_MESSAGES['too-large'] };
    }

    const feed = parseFeedXml(text);
    if (!feed || (!feed.title && feed.episodes.length === 0)) {
      return { feed: null, error: 'not-a-feed', message: FEED_ERROR_MESSAGES['not-a-feed'] };
    }

    return { feed, error: null, message: '' };
  } catch (err) {
    const aborted = err instanceof DOMException && err.name === 'AbortError';
    const kind: FeedErrorKind = aborted ? 'timeout' : 'unreachable';
    return { feed: null, error: kind, message: FEED_ERROR_MESSAGES[kind] };
  } finally {
    clearTimeout(timer);
  }
}

// ── Feed cache ────────────────────────────────────────────────────
//
// Feeds are cached in localStorage keyed by feed URL. Pages read the cache
// synchronously so they render instantly, and a background refresh only
// happens once the entry is stale. A feed is never fetched on every page
// load, and a feed that is failing keeps serving its last good episodes.

const FEED_CACHE_PREFIX = 'bchnostr:podcast-feed:';
/** Refresh at most once an hour per feed. */
export const FEED_REFRESH_INTERVAL_MS = 60 * 60 * 1000;
/** After a failure, back off before trying again. */
const FEED_RETRY_INTERVAL_MS = 10 * 60 * 1000;
/** Episodes retained per feed in the cache (pages show far fewer). */
const CACHE_EPISODE_LIMIT = 60;

interface CachedFeed {
  /** When the cache entry was last written. */
  fetchedAt: number;
  /** Last successful fetch — 0 if we've never succeeded. */
  succeededAt: number;
  /** Last error, if the most recent attempt failed. */
  error: FeedErrorKind | null;
  title: string;
  image: string;
  guid: string;
  episodes: PodcastEpisode[];
}

function cacheKey(feedUrl: string): string {
  return FEED_CACHE_PREFIX + feedUrl;
}

/** Read a cached feed. Returns null when absent or unreadable. */
export function getCachedFeed(feedUrl: string): CachedFeed | null {
  const url = safeFeedUrl(feedUrl);
  if (!url) return null;
  try {
    const raw = localStorage.getItem(cacheKey(url));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedFeed;
    if (!parsed || !Array.isArray(parsed.episodes)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(feedUrl: string, entry: CachedFeed) {
  try {
    localStorage.setItem(cacheKey(feedUrl), JSON.stringify(entry));
  } catch {
    // Storage full / disabled — the feature degrades to "fetch each visit",
    // which is slower but still correct. Never let this throw.
  }
}

/** Is this cache entry due for a background refresh? */
export function isCacheStale(entry: CachedFeed | null): boolean {
  if (!entry) return true;
  const age = Date.now() - entry.fetchedAt;
  // A failed attempt backs off on a shorter timer than a successful one, so
  // a briefly-offline feed recovers quickly without hammering the proxy.
  return age > (entry.error ? FEED_RETRY_INTERVAL_MS : FEED_REFRESH_INTERVAL_MS);
}

/** In-flight fetches, so N components mounting at once cause ONE request. */
const inFlight = new Map<string, Promise<CachedFeed | null>>();

/**
 * Refresh a feed into the cache, coalescing concurrent callers.
 *
 * On failure the previous episodes are PRESERVED — a feed that goes down
 * (or gets its artwork changed, or drops an episode) must never blank out
 * a podcast page that was working a minute ago.
 */
export function refreshFeed(rawFeedUrl: string): Promise<CachedFeed | null> {
  const url = safeFeedUrl(rawFeedUrl);
  if (!url) return Promise.resolve(null);

  const existing = inFlight.get(url);
  if (existing) return existing;

  const promise = (async (): Promise<CachedFeed | null> => {
    const previous = getCachedFeed(url);
    const { feed, error } = await fetchFeed(url);
    const now = Date.now();

    if (!feed) {
      const entry: CachedFeed = {
        fetchedAt: now,
        succeededAt: previous?.succeededAt || 0,
        error,
        title: previous?.title || '',
        image: previous?.image || '',
        guid: previous?.guid || '',
        // Keep serving the last good episodes.
        episodes: previous?.episodes || [],
      };
      writeCache(url, entry);
      return entry;
    }

    const entry: CachedFeed = {
      fetchedAt: now,
      succeededAt: now,
      error: null,
      title: feed.title,
      image: feed.image,
      guid: feed.guid,
      episodes: feed.episodes.slice(0, CACHE_EPISODE_LIMIT),
    };
    writeCache(url, entry);
    return entry;
  })().finally(() => {
    inFlight.delete(url);
  });

  inFlight.set(url, promise);
  return promise;
}

/** Drop a feed's cache entry (used when the creator changes the feed URL). */
export function clearFeedCache(feedUrl: string) {
  const url = safeFeedUrl(feedUrl);
  if (!url) return;
  try { localStorage.removeItem(cacheKey(url)); } catch {}
}

export type { CachedFeed };

// ── Build / parse the show event ──────────────────────────────────

export interface PodcastDraft {
  name: string;
  description: string;
  image: string;
  feedUrl: string;
  website: string;
  hosts: string[];
  category: string;
  bch?: string;
  guid?: string;
}

/** Build tags + content for a PODCAST_KIND publish. */
export function buildPodcastEvent(draft: PodcastDraft): { tags: string[][]; content: string } {
  const category = PODCAST_CATEGORIES.some(c => c.id === draft.category) ? draft.category : 'other';
  const feedUrl = safeFeedUrl(draft.feedUrl);

  const tags: string[][] = [
    ['d', PODCAST_D_TAG],
    ['t', PODCAST_TAG],
    ['t', `${PODCAST_CAT_PREFIX}${category}`],
    ['alt', 'A podcast on BCHNostr'],
  ];
  if (draft.name.trim()) tags.push(['name', clip(draft.name, 200)]);
  if (feedUrl) tags.push(['feed', feedUrl]);
  // NIP-73 external id, so the show is linkable from outside BCHNostr.
  if (draft.guid?.trim()) {
    tags.push(['i', `podcast:guid:${clip(draft.guid, 100)}`]);
    tags.push(['k', 'podcast:guid']);
  }

  const content = {
    name: clip(draft.name, 200),
    description: clip(stripHtml(draft.description, 3000), 3000),
    image: safeUrlLoose(draft.image),
    feedUrl,
    website: safeUrlLoose(draft.website),
    hosts: draft.hosts.map(h => clip(h, 100)).filter(Boolean).slice(0, 10),
    category,
    bch: (draft.bch || '').trim(),
    guid: clip(draft.guid, 100),
  };

  return { tags, content: JSON.stringify(content) };
}

/** Tags + content that retire a show (published when Podcast is turned off). */
export function retirePodcastEvent(): { tags: string[][]; content: string } {
  return {
    tags: [
      ['d', PODCAST_D_TAG],
      ['alt', 'Podcast removed'],
    ],
    content: '',
  };
}

/** Is this event a live (non-retired) podcast show? */
export function isActivePodcast(ev: Event | null | undefined): boolean {
  if (!ev || ev.kind !== PODCAST_KIND) return false;
  const dTag = ev.tags.find(t => t[0] === 'd')?.[1];
  if (dTag !== PODCAST_D_TAG) return false;
  if (!ev.tags.some(t => t[0] === 't' && t[1] === PODCAST_TAG)) return false;
  return ev.content.trim() !== '';
}

/**
 * Parse a podcast show event. Returns null if it isn't an active show, or
 * if it has no usable feed — a show without a feed has nothing to display.
 *
 * Everything here is re-sanitized rather than trusted: the event is signed
 * by its author, which proves authorship, not that the URLs are safe.
 */
export function parsePodcast(ev: Event): PodcastShow | null {
  if (!isActivePodcast(ev)) return null;

  let data: Record<string, unknown> = {};
  try {
    const parsed: unknown = JSON.parse(ev.content);
    if (!parsed || typeof parsed !== 'object') return null;
    data = parsed as Record<string, unknown>;
  } catch {
    return null;
  }

  const tagValue = (name: string) => ev.tags.find(t => t[0] === name)?.[1] || '';
  const feedUrl = safeFeedUrl(
    typeof data.feedUrl === 'string' ? data.feedUrl : tagValue('feed'),
  );
  if (!feedUrl) return null;

  const catTag = ev.tags.find(t => t[0] === 't' && t[1].startsWith(PODCAST_CAT_PREFIX));
  const category = catTag
    ? catTag[1].slice(PODCAST_CAT_PREFIX.length)
    : typeof data.category === 'string' ? data.category : 'other';

  const hosts = Array.isArray(data.hosts)
    ? data.hosts.filter((h): h is string => typeof h === 'string').map(h => clip(h, 100)).filter(Boolean).slice(0, 10)
    : [];

  const guidTag = ev.tags.find(t => t[0] === 'i' && t[1].startsWith('podcast:guid:'));

  return {
    name: clip(typeof data.name === 'string' ? data.name : tagValue('name'), 200) || 'Untitled podcast',
    description: stripHtml(typeof data.description === 'string' ? data.description : '', 3000),
    image: safeUrl(typeof data.image === 'string' ? data.image : ''),
    feedUrl,
    website: safeUrl(typeof data.website === 'string' ? data.website : ''),
    hosts,
    category: PODCAST_CATEGORIES.some(c => c.id === category) ? category : 'other',
    bch: clip(typeof data.bch === 'string' ? data.bch : '', 120),
    guid: guidTag
      ? clip(guidTag[1].slice('podcast:guid:'.length), 100)
      : clip(typeof data.guid === 'string' ? data.guid : '', 100),
    pubkey: ev.pubkey,
    created_at: ev.created_at,
    event: ev,
  };
}

// ── Episode identity & routing ────────────────────────────────────

/**
 * A stable, URL-safe slug for an episode.
 *
 * An RSS guid can be a URL, contain slashes, or be 200 characters long, so
 * it can't go in a path segment as-is. We hash it to a short token and
 * route on `<pubkey>/<token>`, then resolve the token against the feed.
 */
export function episodeSlug(guid: string): string {
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < guid.length; i++) {
    const c = guid.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
    h2 = Math.imul(h2 + c, 0x85ebca6b) >>> 0;
  }
  return h1.toString(36) + h2.toString(36);
}

/** Find an episode by its slug. */
export function findEpisodeBySlug(
  episodes: PodcastEpisode[], slug: string,
): PodcastEpisode | null {
  return episodes.find(e => episodeSlug(e.guid) === slug) || null;
}

/** In-app path for a podcast show. */
export function podcastPath(pubkey: string): string {
  return `/podcasts/${pubkey}`;
}

/** In-app path for an episode. */
export function episodePath(pubkey: string, guid: string): string {
  return `/podcasts/${pubkey}/${episodeSlug(guid)}`;
}

/** NIP-73 external id for an episode, used as the discussion root. */
export function episodeExternalId(guid: string): string {
  return `podcast:item:guid:${guid}`;
}

// ── Follows ───────────────────────────────────────────────────────
//
// "Follow podcast" is intentionally NOT a kind-3 contact-list follow:
// subscribing to a show shouldn't silently reshape someone's social graph.
// It's a small addressable list of show coordinates, one event per user.

/** Build a podcast-follow list event from a set of creator pubkeys. */
export function buildPodcastFollowEvent(pubkeys: string[]): { tags: string[][]; content: string } {
  const unique = [...new Set(pubkeys.filter(pk => /^[0-9a-f]{64}$/i.test(pk)))].slice(0, 500);
  const tags: string[][] = [
    ['d', PODCAST_FOLLOW_D_TAG],
    ['t', PODCAST_FOLLOW_TAG],
    ['alt', 'Podcasts followed on BCHNostr'],
    ...unique.map(pk => ['p', pk]),
  ];
  return { tags, content: JSON.stringify({ podcasts: unique }) };
}

/** Read the followed creator pubkeys out of a follow-list event. */
export function parsePodcastFollows(ev: Event | null | undefined): string[] {
  if (!ev) return [];
  const fromTags = ev.tags
    .filter(t => t[0] === 'p' && typeof t[1] === 'string' && /^[0-9a-f]{64}$/i.test(t[1]))
    .map(t => t[1]);
  if (fromTags.length > 0) return [...new Set(fromTags)];
  try {
    const data = JSON.parse(ev.content) as { podcasts?: unknown };
    if (Array.isArray(data.podcasts)) {
      return [...new Set(
        data.podcasts.filter((pk): pk is string => typeof pk === 'string' && /^[0-9a-f]{64}$/i.test(pk)),
      )];
    }
  } catch {}
  return [];
}

// ── Promotion ─────────────────────────────────────────────────────

export interface PodcastBoostData {
  /** 'show' promotes the podcast page; 'episode' promotes one episode. */
  target: 'show' | 'episode';
  /** The show creator's pubkey. */
  podcastPubkey: string;
  /** Episode guid when target === 'episode'. */
  episodeGuid: string;
  priceSats: number;
  expiresAt: number;
  boostedBy: string;
  bidUsd?: number;
  txid?: string;
}

/** d-tag for a podcast boost. Keyed so re-boosting replaces, never stacks. */
export function podcastBoostDTag(pubkey: string, episodeGuid?: string): string {
  return PODCAST_BOOST_D_TAG_PREFIX + pubkey + (episodeGuid ? `/${episodeSlug(episodeGuid)}` : '');
}

/** Parse a podcast-boost event, sanitizing its expiry. Null if malformed. */
export function parsePodcastBoost(ev: Event): PodcastBoostData | null {
  try {
    const data = JSON.parse(ev.content) as PodcastBoostData;
    if (!data || typeof data.podcastPubkey !== 'string') return null;
    if (!/^[0-9a-f]{64}$/i.test(data.podcastPubkey)) return null;

    let expiresAt = Number(data.expiresAt);
    if (!Number.isFinite(expiresAt) || expiresAt <= 0) return null;
    // Milliseconds sneak in; unix seconds don't reach 10e9 until 2286.
    if (expiresAt > 10_000_000_000) expiresAt = Math.floor(expiresAt / 1000);
    // Cap the window so a fabricated expiry can't buy a permanent slot.
    const maxExpiry = ev.created_at + PODCAST_BOOST_DURATION;
    if (expiresAt > maxExpiry) expiresAt = maxExpiry;

    const txidTag = ev.tags.find(t => t[0] === 'txid')?.[1];

    return {
      target: data.target === 'episode' ? 'episode' : 'show',
      podcastPubkey: data.podcastPubkey,
      episodeGuid: typeof data.episodeGuid === 'string' ? data.episodeGuid : '',
      priceSats: Number.isFinite(data.priceSats) && data.priceSats > 0 ? Math.floor(data.priceSats) : 0,
      expiresAt,
      boostedBy: ev.pubkey,
      bidUsd: typeof data.bidUsd === 'number' ? data.bidUsd : undefined,
      txid: data.txid || txidTag || undefined,
    };
  } catch {
    return null;
  }
}

/** Active podcast boosts, highest bid first. */
export function activePodcastBoosts(events: Event[]): PodcastBoostData[] {
  const now = Math.floor(Date.now() / 1000);
  const byKey = new Map<string, PodcastBoostData>();
  for (const ev of events) {
    const data = parsePodcastBoost(ev);
    if (!data || data.expiresAt <= now || data.priceSats <= 0) continue;
    const key = `${data.podcastPubkey}:${data.episodeGuid}`;
    const existing = byKey.get(key);
    if (!existing || data.priceSats > existing.priceSats) byKey.set(key, data);
  }
  return [...byKey.values()].sort((a, b) => b.priceSats - a.priceSats);
}

// ── Local analytics ───────────────────────────────────────────────
//
// BCHNostr can honestly measure only what happens INSIDE BCHNostr: page
// views and play-button clicks. It cannot see downloads from the
// creator's host, nor how long anyone listened. So we count "play clicks"
// and label them as such — never as "listens" — and the UI states the
// distinction explicitly.
//
// Counters are per-browser localStorage tallies: a lightweight, private
// signal with no tracking, no server, and no cross-user aggregation.
// They are presented as such rather than dressed up as real analytics.

const STATS_KEY = 'bchnostr:podcast-stats';
const STATS_MAX_EPISODES = 200;

export interface PodcastStats {
  /** Views of the show page, keyed by creator pubkey. */
  showViews: Record<string, number>;
  /** Views of an episode page, keyed by `<pubkey>:<slug>`. */
  episodeViews: Record<string, number>;
  /** Play-button clicks, keyed by `<pubkey>:<slug>`. NOT listens. */
  playClicks: Record<string, number>;
}

const EMPTY_STATS: PodcastStats = { showViews: {}, episodeViews: {}, playClicks: {} };

export function getPodcastStats(): PodcastStats {
  try {
    const raw = localStorage.getItem(STATS_KEY);
    if (!raw) return { ...EMPTY_STATS };
    const parsed = JSON.parse(raw) as Partial<PodcastStats>;
    return {
      showViews: parsed.showViews || {},
      episodeViews: parsed.episodeViews || {},
      playClicks: parsed.playClicks || {},
    };
  } catch {
    return { ...EMPTY_STATS };
  }
}

/** Trim a counter map so it can't grow without bound. */
function trim(map: Record<string, number>): Record<string, number> {
  const keys = Object.keys(map);
  if (keys.length <= STATS_MAX_EPISODES) return map;
  const kept = keys
    .sort((a, b) => (map[b] || 0) - (map[a] || 0))
    .slice(0, STATS_MAX_EPISODES);
  const next: Record<string, number> = {};
  for (const k of kept) next[k] = map[k];
  return next;
}

function bump(bucket: keyof PodcastStats, key: string) {
  if (!key) return;
  try {
    const stats = getPodcastStats();
    const map = stats[bucket];
    map[key] = (map[key] || 0) + 1;
    stats[bucket] = trim(map);
    localStorage.setItem(STATS_KEY, JSON.stringify(stats));
  } catch {}
}

export function recordShowView(pubkey: string) {
  bump('showViews', pubkey);
}

export function recordEpisodeView(pubkey: string, guid: string) {
  bump('episodeViews', `${pubkey}:${episodeSlug(guid)}`);
}

/** Record a click on Play. This is a play CLICK, never a confirmed listen. */
export function recordPlayClick(pubkey: string, guid: string) {
  bump('playClicks', `${pubkey}:${episodeSlug(guid)}`);
}

/** Aggregate the local counters for one show. */
export function summarizeStats(pubkey: string): {
  showViews: number;
  episodeViews: number;
  playClicks: number;
} {
  const stats = getPodcastStats();
  const prefix = `${pubkey}:`;
  const sumPrefixed = (map: Record<string, number>) =>
    Object.entries(map).reduce((total, [k, v]) => (k.startsWith(prefix) ? total + v : total), 0);

  return {
    showViews: stats.showViews[pubkey] || 0,
    episodeViews: sumPrefixed(stats.episodeViews),
    playClicks: sumPrefixed(stats.playClicks),
  };
}

// ── Display helpers ───────────────────────────────────────────────

/** Format seconds as "1h 04m" / "42m" / "9m 05s". */
export function formatEpisodeDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

/** Format a unix timestamp as a short date. Empty string when unknown. */
export function formatEpisodeDate(publishedAt: number): string {
  if (!publishedAt) return '';
  return new Date(publishedAt * 1000).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

/** The text posted to Nostr when someone shares an episode. */
export function buildShareText(
  show: PodcastShow, episode: PodcastEpisode, appUrl: string,
): string {
  const lines = [
    `🎙 ${show.name} — ${episode.title}`,
  ];
  // The creator's own episode page, so the share drives traffic to them.
  if (episode.link) lines.push(episode.link);
  lines.push(`Listen & discuss on BCHNostr: ${appUrl}`);
  return lines.join('\n\n');
}
